/**
 * useAiReviewRun — TanStack Query hooks for the async AI Review job
 * (create -> poll, access pattern 3 in the epic brief).
 *
 * Two hooks, not one: `useCreateAiReviewRun` fires the `POST` (202
 * AiReviewAccepted); `useAiReviewRun` polls `GET /ai-reviews/{id}` while the
 * run is non-terminal. The caller (`AiReviewPage`, sub-task 2.6) owns the
 * `reviewId` — it reads `job_id` off the mutation's `onSuccess` and passes it
 * into `useAiReviewRun`, exactly the same two-hook shape `useNewImport`
 * (mutation) + a poll query already establish elsewhere in this route tree.
 *
 * `useAiReviewRun`'s `refetchInterval` mirrors `useImportsList.ts`'s
 * conditional-interval idiom (`useImportsList.ts:61-66`): a function reading
 * `query.state.data`, polling only while non-terminal and stopping (`false`)
 * once the run settles.
 *
 * There are TWO async hops, not one (sub-task 03 R2): scoring (`pending ->
 * processing -> succeeded`) and then building the derived file (`applying ->
 * applied`). They share this one query and one key — the interval simply
 * resumes.
 */

import type {
  AiReviewAccepted,
  AiReviewCreateRequest,
  AiReviewResult,
  AiReviewStatus,
} from "@nu-assist/contracts";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import type { AiReviewApiError, AiReviewClient } from "../../../clients/aiReview.client";

/** Poll cadence while a run is pending/processing. */
const POLL_INTERVAL_MS = 2500;

/**
 * How long the page will wait on an in-flight run before giving up on it.
 *
 * A run CAN strand in a non-terminal status indefinitely, and this is designed
 * behaviour rather than a fault: when the apply hand-off fails,
 * `ai_review_worker/handler.py` deliberately parks the run back on `succeeded`
 * so an operator can recover the scoring the recruiter has already paid Bedrock
 * for. A run left `applying` behaves the same way — `applying -> applying` is
 * not a legal edge, so nothing else ever touches it.
 *
 * Nothing settles it automatically. The ai-review sweeper settles the derived
 * **FILE** (`imports.status -> failed`), NOT the run — stated in
 * `ai_reviews/repository.py` and `ai_review_worker/handler.py`, both in those
 * words. An earlier revision of this module asserted the opposite to justify
 * having no deadline; it was wrong, and it only became harmful when the page
 * started waiting (Trello #711 cl.5, `/pr-review` iter 1 Agent C). Before that
 * the page had already left and nobody was watching.
 *
 * 15 minutes: comfortably past a real scoring + apply cycle, short enough that
 * a recruiter is not left staring at a spinner for the rest of the afternoon.
 */
const STALL_DEADLINE_MS = 15 * 60 * 1000;

/**
 * Poll cadence AFTER the deadline. The deadline changes the MESSAGE, it does
 * not stop the machine.
 *
 * Stopping was the first design and it was wrong twice over
 * (`/pr-review` iter 3 — Agent A blocker 2, Agent C V1). It made abandonment
 * IRREVERSIBLE: polling stopped, so the re-arm triggers froze, so the effect
 * could never run again, so a run that finished five minutes later could never
 * fire the redirect. And it forced a distinction the client cannot actually
 * draw — a QUEUED run is `pending` with `progress: null` (the API emits
 * progress only from `processing`), so "healthy but slow" and "stranded
 * forever" look identical from here.
 *
 * Backing off dissolves the distinction instead of trying to detect it: a slow
 * run still lands and still opens its file, a stranded one costs two requests a
 * minute in a tab the banner has already told the recruiter to leave.
 *
 * Deliberately UNCAPPED — there is no second, harder deadline that finally
 * stops it. A cap would restore the one-way abandonment this exists to remove,
 * for a saving of two requests a minute on a page the recruiter has been told
 * to leave and can close (`/pr-review` iter 4, Agent C minor). Revisit only
 * with evidence of tabs left open for hours.
 */
const STALLED_POLL_INTERVAL_MS = 30_000;

/** Statuses that are non-terminal for EVERY run, preview or full.
 *  `applying` is the SECOND async hop (building the derived file): the run has
 *  finished scoring but the file does not exist yet. */
const NON_TERMINAL_STATUSES: ReadonlySet<AiReviewStatus> = new Set([
  "pending",
  "processing",
  "applying",
]);

/**
 * Is this run still in flight?
 *
 * `succeeded` is the one status whose terminality DEPENDS ON THE RUN, which is
 * why this is a function over the result rather than a second set membership:
 *
 * * A **preview** (`sample_size` set) genuinely ends at `succeeded`. It never
 *   produces a file, `AiReviewsService.create` writes no derived row for it,
 *   and nothing will ever move it on. Polling past it would spin forever.
 * * A **full run** (`sample_size == null`) is auto-applied by the scoring
 *   worker (`auto_apply` on the SQS message), so `succeeded` is a HOP:
 *   `processing -> succeeded -> applying -> applied` (`ai_reviews/domain.py`
 *   `ALLOWED_TRANSITIONS`). A tick landing on it must keep polling.
 *
 * The docstring above this constant used to claim the poll "must survive
 * `succeeded -> applying -> applied`" while `succeeded` was absent from the
 * set, so it did not. That was invisible while the page navigated away on the
 * 202 and nobody watched a run to completion. `ImportDetailPage` is the first
 * caller that does — the recruiter is sent there ON the 202 and the run
 * finishes under them — and with `succeeded` absent it would have stopped one
 * hop short, on the page whose whole job is to show the run landing.
 */
export function isRunInFlight(run: AiReviewResult | undefined): boolean {
  if (!run) return false;
  if (NON_TERMINAL_STATUSES.has(run.status)) return true;
  return run.status === "succeeded" && run.sample_size == null;
}

export interface UseCreateAiReviewRunArgs {
  client: AiReviewClient;
}

/** Fires `POST /ai-reviews`. The caller reads `data.job_id` on success and
 *  feeds it to `useAiReviewRun` to start polling. */
export function useCreateAiReviewRun({ client }: UseCreateAiReviewRunArgs) {
  return useMutation<AiReviewAccepted, AiReviewApiError, AiReviewCreateRequest>({
    mutationFn: (req) => client.createRun(req),
  });
}

// There is no `useApplyAiReview` here any more, and no `UseApplyAiReviewArgs`
// either — the interface outlived the hook by one round. `POST /ai-reviews/{id}/apply`
// still exists — it is how a stranded build is recovered out-of-band, by an
// OPERATOR; this page reports the strand (see `STALL_DEADLINE_MS`) but never
// calls it. No browser path reaches it: the scoring worker claims the apply hop
// itself, and no page offers a retry. The creator navigates away on the 202
// and never sees `applied`; the destination watches the run land and, if it
// strands, says so and points at Contact Lists — reporting, never driving.
// `aiReview.client.applyRun` stays, and its test stays the contract check for
// that endpoint.
//
// Recovering a stranded build is therefore operator-only. Giving the imports
// list a "retry this file" action would put it back in the recruiter's hands;
// that is a follow-up, not part of this page.

export interface UseAiReviewRunArgs {
  client: AiReviewClient;
  /** The run's `job_id`, or `null` before a run has been started — the
   *  query is disabled (no fetch) until this is set. */
  reviewId: string | null;
  sortKey?: string | null;
  sortDir?: "asc" | "desc" | null;
}

export interface UseAiReviewRunResult {
  /** Latest poll result, or `undefined` before the first response. */
  run: AiReviewResult | undefined;
  /** `true` while a run is in flight (queued for its first poll, or
   *  actively pending/processing) — the caller's cue to show the running
   *  state / progress bar. `false` once a terminal status lands, or when no
   *  `reviewId` is set. */
  isPolling: boolean;
  /** The run has sat in flight past {@link STALL_DEADLINE_MS} and this page has
   *  stopped waiting. NOT "the run failed" — a stranded run stays non-terminal
   *  forever (see the constant), so the recruiter has to be sent somewhere
   *  useful rather than left on a spinner. */
  stalled: boolean;
  /** The poll itself is failing (network, 5xx, an expired session). Distinct
   *  from `stalled`: the run may be perfectly healthy and unreachable. */
  pollFailed: boolean;
}

export interface UseLatestAiReviewArgs {
  client: AiReviewClient;
  /** The import whose latest scored run to fetch, or `null`/`undefined` to
   *  disable the query. */
  importId: string | null | undefined;
  /** Gate the fetch (e.g. only when the import carries the `ai` tag). Defaults
   *  to `true`. */
  enabled?: boolean;
  /**
   * React Query's `retry`, forwarded. Defaults to the library's three attempts,
   * which is right for the PREFILL — a transient blip there costs nothing and
   * the form is usable meanwhile.
   *
   * The `?criteriaFrom=` carry passes `false`, and the reason is user-visible:
   * the criteria are held back until the lookup settles, so a bad id (a 422 on
   * a malformed uuid, measured live 2026-09-11) left the page silent for over
   * twenty seconds before it could say the source was unreadable.
   */
  retry?: boolean | number;
}

/**
* One-shot read of an import's latest applied AI Review run (`GET
 *  /ai-reviews/by-import/{id}`) — backs the export-time "view scores +
 *  reasons" panel. `data` is `null` when the import has never had an
 *  applied run.
 *
 * ⚠️ THIS QUERY'S DATA CAN REACH A CONSUMER THAT DID NOT ASK FOR IT.
 *
 * The mechanism, stated before the instance, because the instance has now been
 * tripped over three different doors (PR #869, Agent A): **a React Query
 * instance reads its key's cache entry even when it is `enabled: false`, and
 * whatever any other instance put there.** `enabled` gates the REQUEST, never
 * the READ. The three doors so far: `placeholderData` (below), two instances
 * sharing one key (a live bug on 2026-09-11), and a key another mount left warm
 * (found in review the same day). A caller that must not be seeded by this data
 * needs a guard of its own — not an `enabled` flag.
 *
 * ⚠️ DO NOT ADD `placeholderData` TO THIS QUERY.
 *
 * `AiReviewPage`'s prefill effect is safe against seeding file B's form with
 * file A's criteria ONLY because this query returns `undefined` across a key
 * change instead of A's cached data. Add `placeholderData: keepPreviousData` —
 * a normal change, and one `useImportsList` already makes — and the effect
 * fires while B is selected holding A's payload, records `seededImportIdRef =
 * B`, and leaves A's criteria in the form permanently, labelled as B's. That is
 * the exact bug Lucas reported on the live stack, re-entered by a different
 * door. `useOutputFilenameSuggestion` documents the same hazard for its own
 * query, which is where this note is borrowed from (round 8, Agent C M5).
 */
export function useLatestAiReview({
  client,
  importId,
  enabled = true,
  retry,
}: UseLatestAiReviewArgs) {
  return useQuery<AiReviewResult | null>({
    queryKey: ["ai-review", "by-import", importId],
    queryFn: () => client.getLatestByImport(importId as string),
    enabled: enabled && importId != null,
    ...(retry === undefined ? {} : { retry }),
  });
}

/** Polls `GET /ai-reviews/{id}` while the run is in flight — see
 *  {@link isRunInFlight} for why `succeeded` is not a fixed answer. */
export function useAiReviewRun({
  client,
  reviewId,
  sortKey,
  sortDir,
}: UseAiReviewRunArgs): UseAiReviewRunResult {
  // Armed as a TIMER rather than computed from elapsed time on each render.
  // Renders only happen on poll ticks, so a deadline crossed just after the
  // final tick would never be observed — the polling would stop and the
  // spinner would stay up forever, which is the exact bug this exists to end.
  const [deadlinePassed, setDeadlinePassed] = useState(false);

  const query = useQuery<AiReviewResult>({
    queryKey: ["ai-review", "run", reviewId, sortKey ?? null, sortDir ?? null],
    queryFn: () =>
      client.getRun(reviewId as string, {
        sort_key: sortKey ?? undefined,
        sort_dir: sortDir ?? undefined,
      }),
    enabled: reviewId !== null,
    refetchInterval: (q) => {
      if (!isRunInFlight(q.state.data)) {
        // Terminal: stop. TanStack recomputes this after every poll, so a run
        // that settles between ticks stops on the next one rather than needing
        // anything to cancel it.
        return false;
      }
      // Past the deadline we keep watching, slowly — see
      // `STALLED_POLL_INTERVAL_MS` for why stopping was the wrong shape.
      if (deadlinePassed) return STALLED_POLL_INTERVAL_MS;
      return POLL_INTERVAL_MS;
    },
    // Keep polling even when the AI Review tab is backgrounded. This is an
    // async scoring job (seconds to minutes on a large Apply); a recruiter who
    // starts a run and switches tabs must come back to a settled result, not a
    // frozen "Scoring…" spinner. Without this, TanStack v5 pauses
    // `refetchInterval` while the document is hidden, so the run only ever gets
    // its single initial poll and the UI never observes the terminal status.
    refetchIntervalInBackground: true,
    // A ~2s blip must not flash "we lost contact with the server" and disable
    // the CTA mid-run (`/pr-review` iter 3, Agent C V3). `isError` only becomes
    // true once the retries are exhausted, so raising the count is what puts
    // real distance between a hiccup and a report.
    retry: 3,
  });

  // The deadline measures TIME WITHOUT PROGRESS, not total elapsed time, and it
  // only means anything while the run is still in flight. Both halves were
  // wrong in the first cut of this fix (`/pr-review` iter 2 — Agent A blocker 1
  // and Agent C C1-b, C1-c):
  //
  //  * Keyed on `reviewId` alone, it was a wall-clock budget from creation, so
  //    it fired on runs that were visibly moving.
  //  * Never cancelled on a terminal status, it fired 15 minutes after ANY run
  //    — including a finished preview and a `failed` run — and the page put an
  //    "it's taking too long" banner over both.
  //
  // `progress.scored` is in the key as well as `status`, because the whole of a
  // long scoring pass happens inside one status.
  //
  // WHAT THIS DOES NOT DO, stated because two earlier revisions of this comment
  // (and ADR-0016 §L) claimed it did: it does NOT guarantee that a healthy run
  // is never reported. A QUEUED run sits at `pending` with `progress: null` —
  // the API emits progress only from `processing` — so neither trigger fires
  // and the deadline reaches it (`/pr-review` iter 3, Agent A blocker 2 /
  // Agent C V1). That is now survivable rather than fatal: the deadline backs
  // the poll off instead of stopping it, so the banner is a "this is slow"
  // notice a landing run clears, not a one-way exit. Detecting queued-but-
  // healthy from the client is not possible with what the API emits.
  const observedStatus = query.data?.status;
  const observedProgress = query.data?.progress?.scored;
  // A run waiting on the AI provider moves by REDELIVERY, not by progress:
  // the scoring worker re-stamps `error.retrying.updated_at` when a receive
  // defers AND when the next one starts (2026-09-29; start-of-receive stamp
  // since /pr-review #981 iter 1). That is the run moving, so it restarts the
  // clock too — otherwise a 30-minute outage reads as a strand at minute 15
  // while the worker is visibly working through its retries. A real strand
  // stops stamping, so the deadline still reaches it. `attempt` is the
  // fallback for a marker written before `updated_at` existed.
  const retrying = query.data?.error?.retrying;
  const observedRetryStamp =
    typeof retrying === "object" && retrying !== null
      ? ((retrying as { updated_at?: unknown; attempt?: unknown }).updated_at ??
        (retrying as { attempt?: unknown }).attempt)
      : undefined;
  // `observedStatus`, `observedProgress` and `observedRetryStamp` are RE-ARM
  // TRIGGERS, not values the body reads. The effect's purpose is "restart the
  // clock whenever the run moved", so the dependencies ARE the signal; removing
  // them (as the rule suggests) would restore the wall-clock budget that could
  // abandon a healthy queued run.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see above
  useEffect(() => {
    setDeadlinePassed(false);
    if (reviewId === null) return;
    const timer = setTimeout(() => setDeadlinePassed(true), STALL_DEADLINE_MS);
    return () => clearTimeout(timer);
  }, [reviewId, observedStatus, observedProgress, observedRetryStamp]);

  // A terminal run is not stalled, whatever the clock says.
  const stalled = deadlinePassed && (query.data === undefined || isRunInFlight(query.data));

  // The three ways polling stops, and `isPolling` has to agree with
  // `refetchInterval` on ALL of them or the spinner outlives the polling.
  //
  // `query.isError` is the one a previous revision got wrong, under a comment
  // claiming the two could not disagree (`/pr-review` iter 1 Agent A). On an
  // errored `getRun` there is no `data`, so `refetchInterval` saw `undefined`,
  // read it as terminal and stopped — while `!query.data` here read the same
  // `undefined` as "still loading" and span forever.
  //
  // `!query.data` still means "in flight" for the genuine first-load window,
  // where the initial fetch is running and TanStack has not called
  // `refetchInterval` yet. Errors are excluded ahead of it so that window
  // cannot swallow a failure.
  // NOT gated on `stalled` any more. Past the deadline the page is still
  // watching — slowly — so a run that lands late still opens its file. What
  // changes at the deadline is what the page SAYS, not whether it is looking
  // (`/pr-review` iter 3 — the abandonment used to be irreversible).
  const isPolling =
    reviewId !== null && !query.isError && (!query.data || isRunInFlight(query.data));

  return {
    run: query.data,
    isPolling,
    // Both are "we stopped waiting and the run did not finish" — the page owes
    // the recruiter a way out. They are returned separately because they are
    // different sentences to write: one is "this is taking too long", the other
    // is "we cannot reach the server".
    stalled,
    pollFailed: query.isError,
  };
}
